import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/pFKmjLSbXP2aBhvZt9XSt4/gfdgdgd.png',
  alt: 'Santuario Digital: Mamá Sigue Aquí'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-113">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-pinkLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-114"></div>

      <div className="pointer-events-none absolute inset-0 z-0 hidden overflow-hidden md:block" aria-hidden="true" data-id="element-115">
        <svg className="butterfly-float butterfly-float-one absolute left-[7%] top-[20%] h-11 w-11 text-brand-gold/70" viewBox="0 0 64 64" fill="none" data-id="element-116">
          <path d="M30 31C18 5 4 10 8 27c2 10 12 12 22 7M34 31C46 5 60 10 56 27c-2 10-12 12-22 7" fill="currentColor" opacity="0.65" data-id="element-117" />
          <path d="M30 33C19 42 17 56 30 53M34 33C45 42 47 56 34 53" fill="currentColor" opacity="0.45" data-id="element-118" />
          <path d="M32 26v29" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" data-id="element-119" />
        </svg>
        <svg className="butterfly-float butterfly-float-two absolute right-[8%] top-[40%] h-8 w-8 text-brand-pink/60" viewBox="0 0 64 64" fill="none" data-id="element-120">
          <path d="M30 31C18 5 4 10 8 27c2 10 12 12 22 7M34 31C46 5 60 10 56 27c-2 10-12 12-22 7" fill="currentColor" opacity="0.65" data-id="element-121" />
          <path d="M30 33C19 42 17 56 30 53M34 33C45 42 47 56 34 53" fill="currentColor" opacity="0.45" data-id="element-122" />
          <path d="M32 26v29" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" data-id="element-123" />
        </svg>
        <svg className="butterfly-float butterfly-float-three absolute bottom-[24%] left-[15%] h-6 w-6 text-brand-goldDark/55" viewBox="0 0 64 64" fill="none" data-id="element-124">
          <path d="M30 31C18 5 4 10 8 27c2 10 12 12 22 7M34 31C46 5 60 10 56 27c-2 10-12 12-22 7" fill="currentColor" opacity="0.65" data-id="element-125" />
          <path d="M30 33C19 42 17 56 30 53M34 33C45 42 47 56 34 53" fill="currentColor" opacity="0.45" data-id="element-126" />
          <path d="M32 26v29" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" data-id="element-127" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-128">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-129">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-pink/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm" data-id="element-130">
            <span className="text-sm" data-id="element-131">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-pinkDark" data-id="element-132">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-4 max-w-4xl mx-auto" data-id="element-133">
            {/* Ambient glow behind headline */}
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-134">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-pink/20 via-brand-gold/10 to-brand-pinkDark/20 rounded-full" data-id="element-135" />
            </div>

            {/* Sparkles */}
            <span className="sparkle absolute -top-2 left-[8%] text-brand-gold text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-136">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-pinkLight text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-137">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-goldLight text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-138">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-pink text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-139">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-140">
              <span className="headline-shine text-[28px]" data-id="element-141">
                No tenés que atravesar este duelo en soledad.
              </span>
              <br data-id="element-142" />
              <span className="phrase-glow text-[22px] font-[700]" data-id="element-143"></span>
            </h1>
          </div>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[320px] sm:max-w-md md:max-w-lg lg:max-w-2xl mx-auto mb-2 flex justify-center" data-id="element-144">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '4/3',
            maxHeight: '500px'
          }} data-id="element-145">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" draggable={false} data-id="element-146" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-147">
            <p className="md:text-xl font-bold text-brand-pink font-serif text-[11px]" data-id="element-148">
              Mamá Sigue Aquí — Acompañamiento espiritual completo
            </p>
            <p className="md:text-base text-brand-text/80 font-sans text-[12px]" data-id="element-149">
              Un acompañamiento espiritual con ejercicios de escritura, rituales
              simbólicos y herramientas prácticas para ayudarte a transformar el
              dolor en una nueva forma de sentir su amor.
            </p>
            <p className="md:text-sm text-brand-text/70 font-sans text-[11px] italic" data-id="element-150">
              ❤️ Aprendé a expresar lo que quedó por decir.❤️ Encontrá un lugar
              seguro para tu dolor.❤️ Atravesá las fechas difíciles con
              herramientas concretas.❤️Recordá a mamá desde un lugar de amor y
              paz.
            </p>
          </div>

          <div className="mx-auto mb-12 flex max-w-3xl flex-wrap items-center justify-center gap-2 px-3 text-xs font-medium md:gap-3 md:text-sm" data-id="element-151">
            <div className="inline-flex items-center rounded-full border border-brand-pink/25 bg-white px-2 py-1.5 text-[9px] shadow-sm whitespace-nowrap md:px-3 md:py-2 md:text-xs" data-id="element-152">
              <BookOpen className="mr-1 h-3 w-3 shrink-0 text-brand-pink md:mr-1.5 md:h-4 md:w-4" data-id="element-153" />
              100% PDF
            </div>
            <div className="inline-flex items-center rounded-full border border-brand-pink/25 bg-white px-2 py-1.5 text-[9px] shadow-sm whitespace-nowrap md:px-3 md:py-2 md:text-xs" data-id="element-154">
              <Zap className="mr-1 h-3 w-3 shrink-0 text-brand-pink md:mr-1.5 md:h-4 md:w-4" data-id="element-155" />
              Acceso en 5 min
            </div>
            <div className="inline-flex max-w-full items-center justify-center rounded-full border border-brand-pink/25 bg-white px-3 py-1.5 text-center text-[9px] font-medium leading-snug shadow-sm md:whitespace-nowrap md:px-3 md:py-2 md:text-xs" data-id="element-156">
              🤍 No importa cuánto tiempo haya pasado
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-157">
            <CTAButton variant="pink" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-158">
              <span className="relative z-10" data-id="element-159">
                🌿 → QUIERO MI ACOMPAÑAMIENTO AHORA
              </span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-160">
              <div className="flex items-center" data-id="element-161">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-pink" data-id="element-162" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-163">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-164">
                🖨️ Listo para leer, imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-165">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-pink" data-id="element-166" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-167">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-pink" data-id="element-168" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-pink/15 bg-white/60 py-4 backdrop-blur-sm" data-id="element-169">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm" data-id="element-170">
          <div className="flex items-center" data-id="element-171">
            <span className="font-bold text-brand-pink mr-1.5" data-id="element-172">+300</span>
            personas acompañadas
          </div>
          <div className="flex items-center" data-id="element-173">
            <span className="font-bold text-brand-pink mr-1.5" data-id="element-174">1</span>Tomo
            principal
          </div>
          <div className="flex items-center" data-id="element-175">
            <span className="font-bold text-brand-pink mr-1.5" data-id="element-176">3</span>Bonus
            incluidos
          </div>
          <div className="flex items-center" data-id="element-177">
            <Star className="w-3.5 h-3.5 text-brand-gold fill-brand-gold mr-1" data-id="element-178" />
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-179">
          <div className="flex items-center" data-id="element-180">
            <span className="font-bold text-brand-pink mr-2" data-id="element-181">+300</span>personas
            acompañadas
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink/50 shrink-0" data-id="element-182"></div>
          <div className="flex items-center" data-id="element-183">
            <span className="font-bold text-brand-pink mr-2" data-id="element-184">1</span>Tomo
            principal
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink/50 shrink-0" data-id="element-185"></div>
          <div className="flex items-center" data-id="element-186">
            <span className="font-bold text-brand-pink mr-2" data-id="element-187">3</span>Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink/50 shrink-0" data-id="element-188"></div>
          <div className="flex items-center" data-id="element-189">
            <Star className="w-4 h-4 text-brand-gold fill-brand-gold mr-1" data-id="element-190" />
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};